/** React-free field conversion, extracted unchanged from scene-spec-sheet-model.
 * Validation stays injected: host and native adapter use the same preserveSceneGenSpec contract.
 */
import type {SceneGenSpec, SceneStartAnchorKind} from './scene-gen-spec-types';
export type SceneSpecPreserver = (raw: unknown) => {spec?: SceneGenSpec; droppedKeys: string[]};
export interface SceneSpecFormValue {
  action: string;
  extraRequest: string;
  startAnchorKind: SceneStartAnchorKind | "";
  /**
   * 🎭 시작 기준이 `character-main` 일 때 결속하는 **캐스트 이름**(W3 F07). `""` = 아직 안 골랐다.
   * ★URL 이 아니라 이름이다★ — 실제 기준 이미지는 재생성 시점에 cast[] 에서 라이브 조회한다
   *   (캡슐 JSDoc 의 되덮기 사유). 그래서 폼도 URL 을 들고 다니지 않는다.
   */
  startAnchorCastName: string;
  /** null = 자동(대본·모델이 정하는 종전 동작). 숫자 = 이 씬만 명시 고정. */
  durationSec: number | null;
  /**
   * 🌬 환경음 — 배경에 깔릴 소리의 **글 묘사**(W4 F13 · 시트 ⑤). `""` = 미설정.
   * ★오디오 파일이 아니다★ — 재생성 회차의 영상 생성 지시에 문장으로 실린다.
   * ★"비었다"가 곧 "없다"는 아니다★ — 이 축이 비면 매니페스트(제작 당시 소리)가 그대로 살아난다.
   *   🔇 W2(2026-09-08): «이 컷은 환경음 없음» 토글(F13 3상태)은 걷었다 — 생성 꼬리가 언제나 조용한 서술이라 «없애라»를 따로 말할 필요가 없다.
   */
  ambientSound: string;
  /**
   * 🔔 효과음 메모 — 특정 순간의 소리(W4 F13 · 시트 ⑤). `""` = 미설정.
   * ★유저가 직접 고른 효과음 **파일**(노드 `sfxUrl`/`sfxManual`)과는 다른 축이다★ — 이 글은 프롬프트에
   *   실릴 뿐 그 음원을 덮지 않는다(시트 ⓘ 문구가 약속하는 그 불변식).
   */
  sfxNote: string;
  /**
   * 🔤🎬 **영상 속 자막**(2026-09-03 회장님 C안 · 옴니 컷 전용) — 켜면 다음 생성에서 이 씬의 자막이
   *   영상 픽셀에 새겨진다(캡슐 `captionsInClip` · 분류 SSOT = `@/lib/agent/inclip-captions`).
   * ★노출 게이트는 시트 prop(`inclipCaptionsAvailable`)이 든다★ — 시댄스 회차에 보여 주면
   *   효과 0 토글(막다른 길 헌법 위반)이다.
   */
  captionsInClip: boolean;
}

export const EMPTY_SCENE_SPEC_FORM: SceneSpecFormValue = {
  action: "",
  extraRequest: "",
  startAnchorKind: "",
  startAnchorCastName: "",
  durationSec: null,
  ambientSound: "",
  sfxNote: "",
  captionsInClip: false,
};

/** 캡슐 → 폼. 캡슐이 없거나 필드가 비면 미설정으로 읽는다(하위 호환의 정의). */
export function sceneSpecFormFromSpec(spec: SceneGenSpec | null | undefined): SceneSpecFormValue {
  if (!spec) return { ...EMPTY_SCENE_SPEC_FORM };
  return {
    action: spec.action ?? "",
    extraRequest: spec.extraRequest ?? "",
    startAnchorKind: spec.startAnchor?.kind ?? "",
    startAnchorCastName: spec.startAnchor?.castName ?? "",
    durationSec: typeof spec.durationSec === "number" && spec.durationSec > 0 ? spec.durationSec : null,
    /* 🔇 옛 캡슐의 `null`(종전 F13 «명시 제거»)은 읽기 관용으로 빈 글로 접는다 — W2 뒤 기본이 조용한 서술이라 뜻이 같다. */
    ambientSound: spec.ambientSound ?? "",
    sfxNote: spec.sfxNote ?? "",
    captionsInClip: spec.captionsInClip === true,
  };
}

export function createSceneSpecFieldConverter(preserveSceneGenSpec: SceneSpecPreserver) {
/**
 * 폼 → 캡슐. **기존 캡슐 위에 얹는다**(미지 키·스탬프·아직 UI 가 없는 축을 지우지 않는다).
 * ★값이 미설정이면 키를 지운다★ — 빈 문자열을 저장하면 화면이 "설정됨"으로 거짓말한다.
 * ★upload 는 URL 없이 저장하지 않는다★ — 그 원천이 캡슐 밖에 없어 껍데기만 남는다(W2 미개방 축).
 */
function sceneSpecToSpec(
  form: SceneSpecFormValue,
  base: SceneGenSpec | null | undefined,
): SceneGenSpec | undefined {
  const next: Record<string, unknown> = { ...(base ?? {}) };
  const put = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === "") delete next[key];
    else next[key] = value;
  };
  put("action", form.action.trim());
  /* 🏭 제작 출처 표식(QA 2026-09-06)은 시드된 문장이 ★그대로일 때만★ 산다 — 한 글자라도 고치거나 비우면
     지운다. 남겨 두면 유저가 고친 장면 상세가 "제작 때 것"으로 읽혀 재생성 프롬프트에 실리지 않는다. */
  if ((next.action ?? "") !== (base?.action ?? "")) delete next.actionSource;
  put("extraRequest", form.extraRequest.trim());
  /* 🎭 `character-main` 은 **이름 없이도 저장한다**(리드 결정 · 적대 1R) — `upload` 와 다른 축이다.
     upload 는 URL 이 없으면 아무 데도 못 쓰는 껍데기지만, 이름 없는 character-main 은 "메인샷으로
     시작하겠다, 인물은 아직 안 골랐다"는 **중간 상태**이고 그동안의 거동은 종전 그대로(=keep)로
     정의돼 있다(scene-spec-regen.sceneSpecAnchorMode). ★저장을 드롭하면 셀렉트가 튕긴다★ —
     고른 값이 저장분에 없으니 다음 동기화가 폼을 옛 값으로 되돌린다. */
  const castName = form.startAnchorCastName.trim();
  if (!form.startAnchorKind || (form.startAnchorKind === "upload" && !base?.startAnchor?.url)) {
    delete next.startAnchor;
  } else if (form.startAnchorKind === "character-main") {
    next.startAnchor = castName ? { kind: "character-main", castName } : { kind: "character-main" };
  } else {
    const url = form.startAnchorKind === "upload" ? base?.startAnchor?.url : undefined;
    next.startAnchor = url ? { kind: form.startAnchorKind, url } : { kind: form.startAnchorKind };
  }
  put("durationSec", typeof form.durationSec === "number" && form.durationSec > 0 ? form.durationSec : null);
  /* 🪦 **삭제 자국은 저장 한 번으로 사라진다**(W3 후속 F07 ② · 이슈 #2479) — "'○○'가 삭제되어
     시작 기준이 해제됐어요"는 유저가 그 사실을 **아직 모를 때**만 참인 안내다. 유저가 시작 기준을
     다시 고르거나(=이 함수를 지나는 저장) 아무 축이나 저장하면 그 안내는 이미 소비됐다 — 그대로 두면
     영영 붙어 있는 소음이 되고, 다음 삭제가 왔을 때 새 자국인지 옛 자국인지 구별할 수 없다.
     ★해제 자체를 되돌리지는 않는다★ — `startAnchor` 는 이미 `none` 이고 그건 진짜 상태다. */
  delete next.releasedCastName;
  /* 🔊 소리 두 축(W4 F13) — 기본은 텍스트 축과 **같은 `put` 관용구**다(빈 값 = 키 삭제).
     ⚠️★위 `delete next.releasedCastName` 뒤에 온다★ — 순서가 뒤집혀도 두 축은 서로 다른 키라
     결과가 같지만, 삭제 자국 소거는 "이 함수를 지나는 모든 저장"의 규약이므로 앞에 세워 둔다.

     ★★환경음은 이 관용구의 **유일한 예외**다(세호 QA W-C F13)★★ — `put` 은 "빈 값 = 키 삭제"라
     2상태만 표현할 수 있는데, 환경음은 캡슐이 매니페스트를 **대신하는** 축이라 3번째 상태가 필요하다:
       · `""`  + off:false → 키 삭제(**상속** — 제작 당시 소리를 그대로 쓴다)
       · 문장  + off:false → 그 문장(**설정** — 매니페스트를 대신한다)
       · off:true          → `null`(**명시 제거** — 이 컷은 환경음 없이 굽는다)
     종전엔 세 번째가 없어 유저가 칸을 지워도 그 신호가 여기서 소멸했고(키 삭제 = 상속), 다음
     재생성에 매니페스트 소리가 그대로 다시 실렸다 — 화면에는 지웠다고 보이는데 소리는 계속 났다.
     ★`null` 을 `put` 에 넘기지 않는다★ — `put` 은 `null` 도 삭제로 접으므로 우회가 아니라 소멸이다.
     ★`sfxNote` 에는 이 예외를 **이번 스코프에서** 두지 않는다★ — ⚠️★동형 구멍은 실재한다★(적대 2R
     P2-j 정정): 매니페스트가 `sfxNote` 를 실어 나르고(`cinematicToStory` 매니페스트 조립) 재생성
     조립부가 그것을 **선적재**하므로, 시트에서 메모를 비워도 제작 당시 메모가 그대로 다시 실린다 —
     환경음과 **같은 막다른 길**이다. 종전 이 자리 주석은 "지울 남의 값이 없다"고 적었는데 그건
     **거짓**이었다. 다만 이번 발주의 스코프가 `ambientSound` 단일이라 손대지 않고 **회부**한다.
     ★고칠 때는 이 파일만으로 안 된다★ — 표현형(`string | null`)·병합 판정·조립부 접기까지 환경음과
     같은 4단을 함께 열어야 한다(한쪽만 열면 신호가 중간에서 또 소멸한다). */
  put("ambientSound", form.ambientSound.trim());
  put("sfxNote", form.sfxNote.trim());
  // 🔤🎬 영상 속 자막(C안) — 켜짐만 값이다(꺼짐 = 키 삭제 = 종전 바이트 · put 의 undefined 삭제 관용구).
  put("captionsInClip", form.captionsInClip ? true : undefined);
  // 🧼 저장 관문 통과 — 화면이 만든 값도 소비자와 같은 검증을 받는다(창구 하나 계약).
  return preserveSceneGenSpec(next).spec;
}

  return sceneSpecToSpec;
}
